import React, { PureComponent } from 'react';

import CoreUtils from '../../common/utils/CoreUtils';
import DateTimeUtils from '../../common/utils/DateTimeUtils';
import NumberUtils from '../../common/utils/NumberUtils';

import EnergyValue from '../../common/value-types/EnergyValue';
import EnergyValueMask from '../../common/value-types/EnergyValueMask';
import EnergyValueUtils from '../../common/value-types/EnergyValueUtils';
import EnergyValueInputUtils from '../../common/value-types/EnergyValueInputUtils';

const minutesInOneHour = 60;

export default class EnergyCellEditor extends PureComponent {
  constructor(props) {
    super(props);

    this.editorId = `EnergyCellEditor_${crypto.randomUUID()}`;
    this.updateSchedulesListener = this.onUpdateSchedules.bind(this);

    this.editorContext = null;
    this.minimumResolution = 1;
    this.resolutionCache = {};
    this.currentFocusIndex = null;
    this.pendingSubmission = false;
    this.suspendResolutionUpdate = false;

    this.state = {
      locale: 'en-US',
      scheduleDate: null,
      contractDisplayName: '',
      resolutionOptions: [],
      currentResolution: 1,
      subhourlyHeaders: [],
      subhourlyValues: [],
      subhourlyDisplayValues: [],
      readOnlyVector: [],
      modifiedVector: [],
      overriddenVector: [],
      electableVector: [],
      cutoffAlertVector: []
    };
  }

  componentDidMount() {
    this.props.commandDataModel.addEventListener('updateSchedules', this.updateSchedulesListener);
  }

  componentWillUnmount() {
    this.props.commandDataModel.removeEventListener('updateSchedules', this.updateSchedulesListener);
  }

  render() {
    return (
      <div tabIndex={-1}>
        <table align="center"><tbody>
          <tr>
            <td className="pwx-energy-cell-source-header">{DateTimeUtils.formatShortDate(this.state.scheduleDate, this.state.locale)}</td>
            <td className="pwx-energy-cell-source-header">{this.props.dataField.toUpperCase()}</td>
            <td className="pwx-energy-cell-source-header">{this.state.contractDisplayName}</td>
          </tr>
        </tbody></table>
        <table align="center"><tbody>
          <tr>
            {
              this.state.resolutionOptions.map(resolutionOption => (
                <td key={resolutionOption.key}>
                  <div
                    className={this.resolveResolutionClassName(resolutionOption, this.state.currentResolution)}
                    onClick={this.onResolutionClick.bind(this, resolutionOption)}
                  >
                    {resolutionOption.label}
                  </div>
                </td>
              ))
            }
          </tr>
        </tbody></table>
        <table align="center"><tbody>
          <tr>
            <td></td>
            {
              this.state.subhourlyHeaders.map((header, index) => (
                <td key={index}>
                  <div className="pwx-energy-cell-minute-header">
                    :{NumberUtils.padZerosIfNeeded(header, 2)}
                  </div>
                </td>
              ))
            }
            <td></td>
          </tr>
          <tr>
            <td><div tabIndex={0} onFocus={this.onFocusBackward.bind(this)} /></td>
            {
              this.state.subhourlyDisplayValues.map((displayValue, index) => (
                <td key={this.resolveInputKey(index)}>
                  <input
                    className={this.resolveInputClassName(
                      this.state.modifiedVector[index],
                      this.state.overriddenVector[index],
                      this.state.electableVector[index],
                      this.state.cutoffAlertVector[index]
                    )}
                    id={this.resolveInputId(index)}
                    onBeforeInput={this.onValueBeforeInput.bind(this)}
                    onChange={this.onValueInputChange.bind(this, index)}
                    onFocus={this.onValueFocus.bind(this, index)}
                    onKeyDown={this.onValueKeyDown.bind(this)}
                    value={displayValue}
                    readOnly={this.state.readOnlyVector[index]}
                  />
                </td>
              ))
            } 
            <td><div tabIndex={0} onFocus={this.onFocusForward.bind(this)} /></td>
          </tr>
          </tbody></table>
        </div>
    );
  }

  resolveResolutionClassName(resolutionOption, currentResolution) {
    var className = 'pwx-energy-cell-resolution ';

    className += resolutionOption.disabled
      ? 'pwx-energy-cell-resolution-disabled'
      : resolutionOption.resolution === currentResolution
        ? 'pwx-energy-cell-resolution-active'
        : 'pwx-energy-cell-resolution-normal';

    return className;
  }

  resolveInputClassName(isModified, isOverridden, isElectable, showElectionCutoffAlert) {
    var className = 'pwx-energy-cell-editor ';

    if (isModified)
      className += 'pwx-energy-cell-modified';
    else if (isOverridden)
      className += 'pwx-energy-cell-overridden';
    else {
      className += isElectable
        ? 'pwx-energy-cell-electable'
        : 'pwx-energy-cell-non-electable';
    }

    if (showElectionCutoffAlert)
      className += ' pwx-energy-cell-alert-overlay-cutoff';

    return className;
  }

  resolveInputKey(index) {
    return `Input_${index}`;
  }

  resolveInputId(index) {
    return `${this.editorId}_${this.resolveInputKey(index)}`;
  }

  onEditorHidden() {
    this.editorContext = null;
    this.minimumResolution = 1;
    this.resolutionCache = {};
    this.currentFocusIndex = null;
    this.pendingSubmission = false;
    this.suspendResolutionUpdate = false;

    this.setState({
      locale: 'en-US',
      scheduleDate: null,
      contractDisplayName: '',
      resolutionOptions: [],
      currentResolution: 1,
      subhourlyHeaders: [],
      subhourlyValues: [],
      subhourlyDisplayValues: [],
      readOnlyVector: [],
      modifiedVector: [],
      overriddenVector: [],
      electableVector: [],
      cutoffAlertVector: []
    });
  }

  onEditorHiding() {
  }

  onEditorShowing() {
    if (CoreUtils.isUndefinedOrNull(this.props.dataModel)
      || CoreUtils.isUndefinedOrNull(this.props.scheduleId)
      || CoreUtils.isUndefinedOrNull(this.props.dataField))
      return;

    this.updateEditorState(
      this.updateEditorContext(),
      CoreUtils.isDefinedAndNotNull(this.props.previousResolution)
        ? this.props.previousResolution
        : 1
    );
  }

  onEditorShown() {
  }

  onEditorFocus(focusOnLastValue) {
    this.updateEditorFocus(focusOnLastValue);
  }

  async onUpdateSchedules(scheduleUpdates) {
    if (CoreUtils.isUndefinedOrNull(this.editorContext))
      return;

    scheduleUpdates.forEach(scheduleUpdate => {
      scheduleUpdate.dataFields.forEach(
        dataField => this.processScheduleDataFieldUpdate(scheduleUpdate.scheduleId, dataField)
      );
    });
  }

  processScheduleDataFieldUpdate(scheduleId, dataField) {
    if (CoreUtils.isUndefinedOrNull(this.props.dataModel)
      || CoreUtils.isUndefinedOrNull(this.props.scheduleId)
      || CoreUtils.isUndefinedOrNull(this.props.dataField)) {
      return;
    }

    if (scheduleId !== this.props.scheduleId
      || dataField !== this.props.dataField)
      return;

    this.updateEditorState(this.updateEditorContext(), this.state.currentResolution);
  }

  updateEditorContext() {
    var previousEditorContext = this.editorContext;

    this.editorContext = this.props.dataModel.getEditorContext(
      this.props.scheduleId,
      this.props.dataField
    );

    var localModifiedBuffer = CoreUtils.isDefinedAndNotNull(previousEditorContext)
      ? this.getCurrentEditValue()
      : new EnergyValue(0);

    var localModifiedMask = CoreUtils.isDefinedAndNotNull(previousEditorContext) && !this.pendingSubmission
      ? localModifiedBuffer.getEqualityMask(previousEditorContext.energyValue).inverse()
      : new EnergyValueMask(false);

    this.pendingSubmission = false;

    return {
      localModifiedBuffer: localModifiedBuffer,
      localModifiedMask: localModifiedMask
    };
  }

  updateEditorState(fieldStatusFlags, currentResolution) {
    var previousResolution = this.state.currentResolution;

    var state = {};

    state.locale = this.editorContext.locale;
    state.scheduleDate = this.editorContext.scheduleDate;
    state.contractDisplayName = this.editorContext.contractDisplayName;

    state.currentResolution = currentResolution;

    this.updateResolutionOptions(state);
    this.determineCurrentResolution(state);
    this.initializeResolutionCache(state, fieldStatusFlags);
    this.activateCurrentResolution(state);

    this.setState(state, () => {
      if (state.currentResolution === previousResolution)
        return;

      if (CoreUtils.isDefinedAndNotNull(this.props.onResize))
        this.props.onResize();
    });
  }

  updateEditorFocus(focusOnLastValue) {
    if (CoreUtils.isDefinedAndNotNull(this.currentFocusIndex)) {
      this.setInputFocus(this.currentFocusIndex);
      return;
    }

    var readOnlyVector = this.state.readOnlyVector;
    var allReadOnly = readOnlyVector.every(readOnlyFlag => readOnlyFlag);

    var index = focusOnLastValue
      ? readOnlyVector.length - 1
      : 0;

    while (true) {
      if (index < 0 || index >= readOnlyVector.length)
        break;

      if (!readOnlyVector[index] || allReadOnly) {
        this.setInputFocus(index);
        break;
      }

      if (focusOnLastValue)
        index -= 1;
      else
        index += 1;
    }
  }

  setInputFocus(index) {
    var element = document.getElementById(this.resolveInputId(index));
    if (CoreUtils.isDefinedAndNotNull(element)) {
      element.select();
      element.focus();
    }
  }

  updateResolutionOptions(state) {
    this.minimumResolution = Math.max(
      this.editorContext.overriddenMask.resolution,
      this.editorContext.lockoutMask.resolution,
      this.editorContext.electionCutOffMask.resolution,
      this.editorContext.electionCutoffAlertMask.resolution
    );

    if (this.editorContext.lockoutMask.all)
      this.minimumResolution = Math.max(
        this.minimumResolution,
        this.editorContext.energyValue.resolution
      );

    state.resolutionOptions = this.getResolutionOptions(
      this.editorContext.subhourlyResolution,
      this.minimumResolution
    );
  }

  determineCurrentResolution(state) {
    state.currentResolution = Math.max(
      state.currentResolution,
      this.editorContext.energyValue.resolution,
      this.minimumResolution
    );
  }

  initializeResolutionCache(state, fieldStatusFlags) {
    var effectiveLocalOverwriteMask = fieldStatusFlags.localModifiedMask.and(
      this.editorContext.lockoutMask.inverse(),
      this.editorContext.subhourlyResolution
    );

    var effectiveEnergyValue = this.editorContext.energyValue.overwrite(
      fieldStatusFlags.localModifiedBuffer,
      effectiveLocalOverwriteMask,
      this.editorContext.subhourlyResolution
    );

    if (this.suspendResolutionUpdate)
      return;

    this.resolutionCache = {};
    state.resolutionOptions.forEach(resolutionOption =>
      this.resolutionCache[resolutionOption.resolution] = EnergyValueUtils.rescale
        (
          effectiveEnergyValue.values,
          EnergyValueUtils.averageValue,
          resolutionOption.resolution
        ));
  }

  updateResolutionCache(state) {
    if (this.suspendResolutionUpdate)
      return;

    this.resolutionCache = {};
    state.resolutionOptions.forEach(resolutionOption =>
      this.resolutionCache[resolutionOption.resolution] = EnergyValueUtils.rescale
        (
          state.subhourlyValues,
          EnergyValueUtils.averageValue,
          resolutionOption.resolution
        ));
  }

  activateCurrentResolution(state) {
    state.subhourlyValues = this.resolutionCache[state.currentResolution];
    state.subhourlyDisplayValues = state.subhourlyValues.map(
      value => EnergyValueInputUtils.convertValueToDisplayString(value, this.editorContext.locale)
    );

    var headerSize = minutesInOneHour / state.currentResolution;

    state.subhourlyHeaders = [];
    for (var index = 0; index < state.currentResolution; index += 1)
      state.subhourlyHeaders.push(index * headerSize);

    state.readOnlyVector = EnergyValueUtils.upscaleResolution(
      this.editorContext.lockoutMask.maskValues,
      state.currentResolution
    );

    state.modifiedVector = EnergyValueUtils.upscaleResolution(
      this.editorContext.modifiedMask.maskValues,
      state.currentResolution
    );

    state.overriddenVector = EnergyValueUtils.upscaleResolution(
      this.editorContext.overriddenMask.maskValues,
      state.currentResolution
    );

    state.electableVector = EnergyValueUtils.upscaleResolution(
      this.editorContext.electionCutOffMask.inverse().maskValues,
      state.currentResolution
    );

    state.cutoffAlertVector = EnergyValueUtils.upscaleResolution(
      this.editorContext.electionCutoffAlertMask.maskValues,
      state.currentResolution
    );
  }

  getCurrentEditValue() {
    return CoreUtils.isDefinedAndNotNull(this.state.subhourlyValues)
      ? new EnergyValue(Array.from(this.state.subhourlyValues))
      : null;
  }

  commitEnergyChanges() {
    var currentEditValue = this.getCurrentEditValue();

    if (CoreUtils.isUndefinedOrNull(currentEditValue))
      return;

    this.pendingSubmission = true;

    this.props.dataModel.submitEditorUpdate(
      this.props.scheduleId,
      this.props.dataField,
      currentEditValue
    );
  }

  getResolutionOptions(subhourlyResolution, minimumResolution) {
    var resolutions = [];
    for (var resolution = subhourlyResolution; resolution >= 1; resolution -= 1) {
      if (subhourlyResolution % resolution === 0)
        resolutions.push({
          key: `Resolution_${resolution}`,
          label: `${minutesInOneHour / resolution}min`,
          resolution: resolution,
          disabled: resolution < minimumResolution
        });
    }

    return resolutions;
  }

  onResolutionClick(resolutionOption) {
    if (resolutionOption.resolution === this.state.currentResolution
      || resolutionOption.disabled)
      return;

    this.currentFocusIndex = null;
    this.suspendResolutionUpdate = true;

    var state = {};

    state.currentResolution = resolutionOption.resolution;
    this.activateCurrentResolution(state);

    this.setState(state, () => {
      if (CoreUtils.isDefinedAndNotNull(this.props.onResize))
        this.props.onResize();

      if (this.state.readOnlyVector.some(readOnlyFlag => !readOnlyFlag))
        this.commitEnergyChanges();

      this.updateEditorFocus(false);
    });
  }

  onValueBeforeInput(event) {
    var editorValue = EnergyValueInputUtils.generateEditorString(
      event.data,
      event.target
    );

    var value = EnergyValueInputUtils.tryParseEditorString(editorValue);
    if (CoreUtils.isUndefinedOrNull(value))
      event.preventDefault();
  }

  onValueInputChange(index, event) {
    this.suspendResolutionUpdate = false;

    var subhourlyDisplayValues = Array.from(this.state.subhourlyDisplayValues);
    subhourlyDisplayValues[index] = event.currentTarget.value;

    var value = parseInt(subhourlyDisplayValues[index], 10);

    var subhourlyValues = Array.from(this.state.subhourlyValues);
    subhourlyValues[index] = EnergyValueUtils.isValidValue(value) && value !== -0
      ? value
      : 0;

    var state = {
      resolutionOptions: this.state.resolutionOptions,
      subhourlyValues: subhourlyValues,
      subhourlyDisplayValues: subhourlyDisplayValues
    };

    this.updateResolutionCache(state);
    this.setState(state);
  }

  onValueFocus(index) {
    this.currentFocusIndex = index;
  }

  onValueKeyDown(event) {
    if (CoreUtils.isUndefinedOrNull(this.props.onCloseEditor))
      return;

    if (event.key === 'Tab')
      this.commitEnergyChanges();
    if (event.key === 'Escape')
      this.props.onCloseEditor(false);
    else if (event.key === 'Enter') {
      this.commitEnergyChanges();
      this.props.onCloseEditor(true);
    }
  }

  onFocusBackward() {
    if (CoreUtils.isDefinedAndNotNull(this.props.onFocusBackward))
      this.props.onFocusBackward(
        this.props.scheduleId,
        this.props.dataField,
        this.state.currentResolution
      );
  }

  onFocusForward() {
    if (CoreUtils.isDefinedAndNotNull(this.props.onFocusForward))
      this.props.onFocusForward(
        this.props.scheduleId,
        this.props.dataField,
        this.state.currentResolution
     );
  }
}